import React, { useEffect, useState, createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { decrypt } from '../../utils/crypto';
import { api } from '../../utils/api';
import { LOGIN_SUCCESS } from '../../constants';
import { Loader } from '../';

/**
 * Context to share auth restoration state
 */
const AuthRestorationContext = createContext({
    isRestoring: true
});

export const useAuthRestoration = () => useContext(AuthRestorationContext);

/**
 * Component to restore authentication state from localStorage after config is loaded
 * This prevents users from being logged out on page refresh and preserves current route
 * 
 * SECURITY: Validates token with server before restoring to prevent "ghost login"
 * NOTE: Uses silent validation (direct API call) to avoid triggering navigation
 */
export const AuthInitializer = ({ children }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((state) => state.auth);
    const { _id, auth_token } = userData;
    const [restoreAttempted, setRestoreAttempted] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isRestoring, setIsRestoring] = useState(true);

    useEffect(() => {
        const restoreAuthState = async () => {
            // Only restore if userData is not already loaded (no _id) and not currently validating
            if (!_id && !restoreAttempted && !isValidating) {
                try {
                    const storedData = localStorage.getItem('userData');
                    if (storedData) {
                        const decryptedData = JSON.parse(decrypt(storedData));
                        
                        // Only restore if we have valid user data
                        if (decryptedData && decryptedData._id && decryptedData.auth_token) {
                            setIsValidating(true);
                            
                            // Set API token temporarily for validation request
                            api.defaults.headers.common["Authorization"] = `Bearer ${decryptedData.auth_token}`;
                            
                            // SECURITY: Validate token with server silently (direct API call, no navigation)
                            // This prevents "ghost login" with expired/revoked tokens
                            try {
                                const response = await api.get('users/login/success');
                                
                                if (response.status === 200 && response.data?.data) {
                                    // Token is valid - restore state silently without navigation
                                    const { data } = response.data;
                                    dispatch({
                                        type: LOGIN_SUCCESS,
                                        data: data
                                    });
                                    
                                    // Update localStorage with fresh data from server
                                    const { encrypt } = require('../../utils/crypto');
                                    const encryptData = encrypt(JSON.stringify(data));
                                    localStorage.setItem("userData", encryptData);
                                    
                                    // Ensure token is set
                                    if (data?.auth_token) {
                                        api.defaults.headers.common["Authorization"] = `Bearer ${data.auth_token}`;
                                    }
                                    
                                    setRestoreAttempted(true);
                                    setIsValidating(false);
                                    setIsRestoring(false);
                                } else {
                                    // Token invalid or expired - clear storage
                                    localStorage.removeItem('userData');
                                    delete api.defaults.headers.common["Authorization"];
                                    setRestoreAttempted(true);
                                    setIsValidating(false);
                                    setIsRestoring(false);
                                }
                            } catch (validationError) {
                                // Validation failed - clear storage
                                // Don't log to avoid console noise on expected failures
                                if (validationError?.response?.status !== 401) {
                                    console.warn('Token validation failed:', validationError);
                                }
                                localStorage.removeItem('userData');
                                delete api.defaults.headers.common["Authorization"];
                                setRestoreAttempted(true);
                                setIsValidating(false);
                                setIsRestoring(false);
                            }
                        } else {
                            setRestoreAttempted(true);
                            setIsRestoring(false);
                        }
                    } else {
                        setRestoreAttempted(true);
                        setIsRestoring(false);
                    }
                } catch (error) {
                    // If decryption fails, retry after a short delay (config might still be loading)
                    if (!restoreAttempted) {
                        setTimeout(async () => {
                            try {
                                const storedData = localStorage.getItem('userData');
                                if (storedData) {
                                    const decryptedData = JSON.parse(decrypt(storedData));
                                    if (decryptedData && decryptedData._id && decryptedData.auth_token) {
                                        setIsValidating(true);
                                        api.defaults.headers.common["Authorization"] = `Bearer ${decryptedData.auth_token}`;
                                        
                                        try {
                                            const response = await api.get('users/login/success');
                                            if (response.status === 200 && response.data?.data) {
                                                const { data } = response.data;
                                                dispatch({
                                                    type: LOGIN_SUCCESS,
                                                    data: data
                                                });
                                                const { encrypt } = require('../../utils/crypto');
                                                const encryptData = encrypt(JSON.stringify(data));
                                                localStorage.setItem("userData", encryptData);
                                                if (data?.auth_token) {
                                                    api.defaults.headers.common["Authorization"] = `Bearer ${data.auth_token}`;
                                                }
                                            } else {
                                                localStorage.removeItem('userData');
                                                delete api.defaults.headers.common["Authorization"];
                                            }
                                        } catch (validationError) {
                                            localStorage.removeItem('userData');
                                            delete api.defaults.headers.common["Authorization"];
                                        }
                                        setIsValidating(false);
                                        setIsRestoring(false);
                                    }
                                }
                            } catch (retryError) {
                                console.warn('Could not restore auth state from localStorage after retry:', retryError.message);
                            }
                            setRestoreAttempted(true);
                            setIsRestoring(false);
                        }, 500); // Retry after 500ms
                    } else {
                        console.warn('Could not restore auth state from localStorage:', error.message);
                    }
                }
            } else if (_id && auth_token) {
                // Ensure API token is set even if userData is already in Redux
                // This handles cases where Redux state exists but token wasn't set
                if (!api.defaults.headers.common["Authorization"]) {
                    api.defaults.headers.common["Authorization"] = `Bearer ${auth_token}`;
                }
                // Auth already exists, no need to restore
                setIsRestoring(false);
            }
        };

        restoreAuthState();
    }, [_id, auth_token, dispatch, restoreAttempted, isValidating]);

    // Provide restoration state via context
    return (
        <AuthRestorationContext.Provider value={{ isRestoring: isRestoring && !_id }}>
            {isRestoring && !_id ? (
                <Loader open={true} />
            ) : (
                children
            )}
        </AuthRestorationContext.Provider>
    );
};
